import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register core (not datalabels globally)
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ----- Responsive, card-friendly layout -----
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px 15px;
  box-sizing: border-box;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 6px 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 6px;
`;

/* Flexible chart area that grows to fill leftover space */
const ChartArea = styled.div`
  flex: 1;
  width: 100%;
  min-height: 220px;
  position: relative;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;
// ---------------------------------------------------

const DomesticVsInternationalSalesBar = ({ salesData = [] }) => {
  const [chartData, setChartData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");

  const monthNames = [
    "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
    "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
  ];

  // Palette
  const GREEN_FILL = "rgba(46, 125, 50, 0.7)";
  const GREEN_STROKE = "#2e7d32";
  const ORANGE_FILL = "rgba(251, 140, 0, 0.7)";
  const ORANGE_STROKE = "#fb8c00";

  useEffect(() => {
    if (!salesData.length) return;
    const years = [...new Set(salesData.map((row) => row.Rok.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear(years.includes("2024") ? "2024" : years[years.length - 1]);
  }, [salesData]);

  useEffect(() => {
    if (!salesData.length || !selectedYear) return;

    let domestic = 0;
    let international = 0;

    salesData.forEach((row) => {
      if (row.Rok.toString() !== selectedYear) return;
      if (selectedMonth !== "all" && parseInt(row.Miesiac) !== parseInt(selectedMonth)) return;

      const code = row.KodKrajuNadaniaTIN?.trim() || "Unknown";
      const revenue = ["K_15","K_19","K_10","K_21","K_17","K_11","K_22"]
        .reduce((sum, key) => sum + (parseFloat(row[key]) || 0), 0);

      if (code === "PL") domestic += revenue;
      else international += revenue;
    });

    const total = domestic + international;
    const pctDomestic = total > 0 ? (domestic / total) * 100 : 0;
    const pctInternational = total > 0 ? (international / total) * 100 : 0;

    setChartData({
      labels: ["Krajowe", "Międzynarodowe"],
      datasets: [
        {
          label: "Udział w Sprzedaży (%)",
          data: [pctDomestic, pctInternational],
          backgroundColor: [GREEN_FILL, ORANGE_FILL],
          borderColor: [GREEN_STROKE, ORANGE_STROKE],
          borderWidth: 1,
          borderRadius: 6,
          maxBarThickness: 64,
        },
      ],
    });
  }, [salesData, selectedYear, selectedMonth]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Proporcja sprzedaży krajowej i międzynarodowej
        <InfoIconWrapper
          data-tooltip-id="domestic-vs-international-tooltip"
          data-tooltip-content={
            "Proporcja sprzedaży\nPokazuje udział sprzedaży krajowej (PL) i międzynarodowej\nw wybranym okresie."
          }
          data-tooltip-place="left"
        >
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>

      <InfoTooltip
        id="domestic-vs-international-tooltip"
        place="left"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "white" }}
      />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Miesiąc:</label>
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            <option value="all">Cały rok</option>
            {monthNames.map((name, idx) => (
              <option key={idx} value={idx + 1}>{name}</option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      {chartData ? (
        <ChartArea>
          <Bar
            data={chartData}
            plugins={[ChartDataLabels]}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: {
                  grid: { display: false },
                  ticks: { font: { size: 11 } },
                },
                y: {
                  beginAtZero: true,
                  max: 100,
                  title: { display: true, text: "Udział (%)" },
                  ticks: {
                    callback: (v) => `${v}%`,
                    stepSize: 20,
                  },
                },
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`,
                  },
                },
                datalabels: {
                  color: "#333",
                  anchor: "end",
                  align: "end",
                  offset: 4,
                  font: { size: 12, weight: "bold" },
                  formatter: (value) => `${Number(value).toFixed(1)}%`,
                },
                title: { display: false },
              },
              animation: { duration: 350 },
            }}
          />
        </ChartArea>
      ) : (
        <p style={{ textAlign: "center", marginTop: 8 }}>Ładowanie danych...</p>
      )}
    </ChartWrapper>
  );
};

export default DomesticVsInternationalSalesBar;
